import * as THREE from 'three';
import {isSketchArcObject} from '../../../mesh/guards/isSketchArcObject';
import {isSketchCircleObject} from '../../../mesh/guards/isSketchCircleObject';
import type {ReferencePlane} from '../../../reference/ReferencePlane';
import {AbstractSketchNearestPointProjector} from './AbstractSketchNearestPointProjector';

/**
 * 圆族草图边解析最近点投影器。
 *
 * 职责：
 * 1. 完整圆按“圆心到鼠标的局部方向”投影到精确半径；
 * 2. 圆弧先投影到支撑圆，投影角不在扫掠范围内时改取最近端点；
 * 3. 全程使用草图参考面的局部二维参数，保证倾斜草图面的投影语义一致。
 */
export class SketchCircleFamilyNearestPointProjector extends AbstractSketchNearestPointProjector {
    /** 复用的鼠标局部坐标，避免捕捉热路径重复分配。 */
    private readonly mouseLocal = new THREE.Vector2();

    /** 复用的圆心局部坐标，避免捕捉热路径重复分配。 */
    private readonly centerLocal = new THREE.Vector2();

    /** 复用的圆心到鼠标方向。 */
    private readonly centerToMouse = new THREE.Vector2();

    /** 复用的解析投影世界坐标。 */
    private readonly projectedWorld = new THREE.Vector3();

    /** 复用的解析圆弧起点世界坐标。 */
    private readonly arcStartWorld = new THREE.Vector3();

    /** 复用的解析圆弧终点世界坐标。 */
    private readonly arcEndWorld = new THREE.Vector3();

    /**
     * 判断目标是否属于完整圆或圆弧。
     *
     * @param targetObject 待判断草图对象。
     * @returns 目标属于圆族时返回 true。
     */
    public supports(targetObject: THREE.Object3D): boolean {
        return isSketchCircleObject(targetObject) || isSketchArcObject(targetObject);
    }

    /**
     * 将世界坐标投影到完整圆或有限圆弧的解析几何上。
     *
     * @param targetObject 目标圆或圆弧。
     * @param worldPosition 待投影世界坐标。
     * @returns 解析曲线上的最近世界坐标；目标类型不受支持时返回 null。
     */
    public project(
        targetObject: THREE.Object3D,
        worldPosition: THREE.Vector3,
    ): THREE.Vector3 | null {
        if (isSketchCircleObject(targetObject)) {
            return this.projectToCircle(
                targetObject.referencePlane,
                targetObject.getCenterWorld(),
                targetObject.getRadius(),
                worldPosition,
            );
        }
        if (!isSketchArcObject(targetObject)) {
            return null;
        }

        const definition = targetObject.getArcDefinition();
        const projected = this.projectToCircle(
            targetObject.referencePlane,
            definition.centerWorld,
            definition.radius,
            worldPosition,
        );
        if (!projected) {
            return null;
        }

        // 支撑圆投影只有落在圆弧真实扫掠范围内时才是圆弧最近点。
        const projectedLocal = targetObject.referencePlane.worldToLocalTo(
            projected,
            this.mouseLocal,
        );
        const projectedAngle = Math.atan2(
            projectedLocal.y - this.centerLocal.y,
            projectedLocal.x - this.centerLocal.x,
        );
        if (this.isAngleInArcRange(
            projectedAngle,
            definition.startAngle,
            definition.endAngle,
            definition.clockwise,
        )) {
            return projected;
        }

        // 投影角位于圆弧缺口时，有限圆弧的最近点只能是两个解析端点之一。
        targetObject.referencePlane.localToWorldTo(
            this.centerLocal.x + definition.radius * Math.cos(definition.startAngle),
            this.centerLocal.y + definition.radius * Math.sin(definition.startAngle),
            this.arcStartWorld,
        );
        targetObject.referencePlane.localToWorldTo(
            this.centerLocal.x + definition.radius * Math.cos(definition.endAngle),
            this.centerLocal.y + definition.radius * Math.sin(definition.endAngle),
            this.arcEndWorld,
        );
        const closestEndpoint = worldPosition.distanceToSquared(this.arcStartWorld)
            <= worldPosition.distanceToSquared(this.arcEndWorld)
            ? this.arcStartWorld
            : this.arcEndWorld;
        return closestEndpoint.clone();
    }

    /**
     * 将世界坐标投影到指定参考面内的解析圆。
     *
     * @param referencePlane 圆所属参考面。
     * @param centerWorld 圆心世界坐标。
     * @param radius 圆半径。
     * @param worldPosition 待投影世界坐标。
     * @returns 解析圆上的世界坐标；圆定义无效时返回 null。
     */
    private projectToCircle(
        referencePlane: ReferencePlane,
        centerWorld: THREE.Vector3,
        radius: number,
        worldPosition: THREE.Vector3,
    ): THREE.Vector3 | null {
        if (!Number.isFinite(radius) || radius <= 0) {
            return null;
        }

        referencePlane.worldToLocalTo(worldPosition, this.mouseLocal);
        referencePlane.worldToLocalTo(centerWorld, this.centerLocal);
        this.centerToMouse.subVectors(this.mouseLocal, this.centerLocal);

        // 圆心处没有唯一最近方向；使用参考面 X 正方向给出确定的解析圆上点。
        if (this.centerToMouse.lengthSq() <= Number.EPSILON) {
            this.centerToMouse.set(1, 0);
        } else {
            this.centerToMouse.normalize();
        }

        const projectedX = this.centerLocal.x + this.centerToMouse.x * radius;
        const projectedY = this.centerLocal.y + this.centerToMouse.y * radius;
        referencePlane.localToWorldTo(projectedX, projectedY, this.projectedWorld);

        // 捕捉结果会跨帧保存，必须返回稳定副本，不能泄漏类内热路径缓存。
        return this.projectedWorld.clone();
    }

    /**
     * 判断角度是否位于有限圆弧的有向扫掠范围内。
     *
     * @param angle 待判断角度。
     * @param startAngle 圆弧起始角。
     * @param endAngle 圆弧终止角。
     * @param clockwise 是否顺时针扫掠。
     * @returns 角度位于圆弧范围时返回 true。
     */
    private isAngleInArcRange(
        angle: number,
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
    ): boolean {
        const fullTurn = Math.PI * 2;
        const normalizedAngle = this.normalizeAngle(angle, fullTurn);
        const normalizedStart = this.normalizeAngle(startAngle, fullTurn);
        const normalizedEnd = this.normalizeAngle(endAngle, fullTurn);

        if (clockwise) {
            return normalizedStart >= normalizedEnd
                ? normalizedAngle <= normalizedStart && normalizedAngle >= normalizedEnd
                : normalizedAngle <= normalizedStart || normalizedAngle >= normalizedEnd;
        }
        return normalizedStart <= normalizedEnd
            ? normalizedAngle >= normalizedStart && normalizedAngle <= normalizedEnd
            : normalizedAngle >= normalizedStart || normalizedAngle <= normalizedEnd;
    }

    /**
     * 将任意角度规范化到单圈范围。
     *
     * @param angle 原始角度。
     * @param fullTurn 单圈弧度。
     * @returns `[0, fullTurn)` 范围内的角度。
     */
    private normalizeAngle(angle: number, fullTurn: number): number {
        return ((angle % fullTurn) + fullTurn) % fullTurn;
    }
}
